import React, { useState, useEffect, FormEvent } from 'react';
import { navigate } from '@reach/router';
import {
  Stack,
  SearchIcon,
  Element,
  SearchInput,
  merge,
  InputGroup,
  InputField,
  InputAddon,
  Button
} from '@quotient/riq-react-ui';
import { getUrlParameter, generateSignInLink } from '@quotient/riq-helpers';
import { AppContext } from '@quotient/riq-state';
import { ShoppingListComp } from './ShoppingListComp';

interface ICarrierProps {
  showSortBy?: boolean;
  showCategory?: boolean;
  onSearchSubmit?: (q: string) => void;
  ref?: React.Ref<unknown>;
}

// @TODO: Add cartet inverted as an icon
const styles = {
  caretInverted: {
    position: 'absolute',
    height: 0,
    width: 0,
    content: '""',
    zIndex: 11,
    top: '-10px',
    left: '50px',
    borderRight: '10px solid transparent',
    borderBottom: '10px solid #fff',
    borderLeft: '10px solid transparent'
  },
  carrier: {
    backgroundColor: 'grays.400',
    padding: 2,
    height: ['44px', 'auto'],
    paddingRight: [0, 8],
    marginTop: 0
  },
  carrierSticky: {
    position: 'fixed',
    zIndex: 2,
    top: [0, '45px', '45px', 0],
    width: '100%'
  },
  searchInputBox: {
    border: '1px solid',
    borderRight: '0',
    borderColor: 'primary',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4
  },
  searchInputButton: {
    width: '49px',
    backgroundColor: 'primary',
    border: '1px solid primary',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4
  },
  searchInputBoxMobile: { flex: 1, justifyContent: 'flex-end' },
  searchInputBoxMobileSticky: {
    position: ['fixed', 'fixed', 'fixed', 'initial'],
    zIndex: 2,
    top: '55.5px',
    right: '45px',
    flex: 1,
    justifyContent: 'flex-end'
  }
};

const Carrier: React.FC<ICarrierProps> = React.forwardRef(function Carrier({ onSearchSubmit, ...props }, ref) {
  const query = getUrlParameter('q');

  const [q, setQ] = useState(query || '');
  const { appState, setAppState }: any = React.useContext(AppContext);
  const { isUserLoggedIn } = appState?.getCommonData?.CI_SigninSignupLinks || {};
  const [searchContent, setSearchContent] = React.useState(false);
  const [isSticky, setSticky] = React.useState(false);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => {
      // Remove scroll event after the component is unmount
      document.removeEventListener('scroll', () => handleScroll);
    };
  }, []);

  const handleScroll = (e: any) => {
    if (e.target.scrollingElement.scrollTop >= 130) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  };

  const onInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.value.length > 0) {
      setSearchContent(true);
    } else {
      setSearchContent(false);
    }
    setQ(e.currentTarget.value);
  };

  const changeSearchContent = (value: boolean) => {
    setSearchContent(value);
  };

  const checkOnKey = (e: React.KeyboardEvent) => {
    if (e.keyCode === 13) updateSearchQuery(q);
    else if (e.keyCode === 27) updateSearchQuery('');
  };

  const checkOnClick = (e: FormEvent) => {
    e.preventDefault();
    updateSearchQuery(q);
  };

  const clearSearchBox = () => {
    updateSearchQuery('');
  };

  const updateSearchQuery = (q: string) => {
    if (q === '') {
      setQ(q);
      setSearchContent(false);
      return;
    }
    if (query === q) return;
    navigate('/search?q=' + q);
  };

  useEffect(() => {
    setAppState({ isCarrierMenuOpen: true });
  }, []);

  const list = appState?.getCommonData?.offcanvasShoppingList;
  const shoppinglistData = {
    listId: list?.listId || '',
    listIdentifier: list?.listIdentifier ? 'DGDC Account: ' + list.listIdentifier : '',
    showListIdentifier: list?.listItems?.showListIdentifier || true,
    sortable: list?.listItems?.sortable || false,
    sortfilter_data: list?.listItems?.sortfilter_data || [],
    sortfilter: list?.listItems?.sortfilter || '',
    arrItems: list?.listItems?.arrItems || [],
    isUserLoggedIn,
    signinlink: generateSignInLink('/')
  };

  return (
    <Stack
      component="Carrier"
      ref={ref}
      marginY={2}
      align="center"
      justify="flex-end"
      css={merge(isSticky ? styles.carrierSticky : {}, styles.carrier)}
      {...props}
    >
      <Stack align="center" gap={12} css={isSticky ? styles.searchInputBoxMobileSticky : styles.searchInputBoxMobile}>
        <Element as="form" onSubmit={checkOnClick} css={{ display: ['none', 'flex'], flexBasis: '25%' }}>
          <InputGroup size="large">
            <InputField
              placeholder="Search here..."
              value={q}
              type="search"
              onChange={onInputChange}
              css={styles.searchInputBox}
            />
            <InputAddon>
              {q.length ? (
                <Element css={{ position: 'absolute', right: '275px', cursor: 'pointer' }} onClick={clearSearchBox}>
                  X
                </Element>
              ) : (
                <span></span>
              )}
              <Button type="submit" variant="primary" size="large" css={styles.searchInputButton}>
                <SearchIcon fill="black" />
              </Button>
            </InputAddon>
          </InputGroup>
        </Element>
        <SearchInput
          css={merge(
            {
              display: ['flex', 'none'],
              position: 'absolute',
              width: '100%',
              height: '44px',
              top: '130px',
              left: 0,
              justifyContent: 'flex-end'
            },
            isSticky ? { position: 'fixed', zIndex: 2, top: '0', left: '0', width: '100%' } : {}
          )}
          placeholder="Search here..."
          value={q}
          searchContent={searchContent}
          updateSearchContent={changeSearchContent}
          onSubmit={checkOnClick}
          onKeyDown={checkOnKey}
          onChange={onInputChange}
          updateValue={updateSearchQuery}
          buttonTextColor="black"
        />
        <ShoppingListComp data={shoppinglistData} />
      </Stack>
    </Stack>
  );
});

export default Carrier;
